/** @module FormulatedProduct/Food/FoodForm */
import crews from "csi/access/crews";
import config from "csi/config";
import def from "csi/def";
import Action from "csi/def/Action";
import FieldConstructor, { Field } from "csi/def/Field";
import Relationship from "csi/def/Relationship";
import { View } from "csi/def/View";
import ViewRef from "csi/def/ViewRef";
import Widget from "csi/form/Widget";
import html from "csi/html";
import locale from "csi/locale";
import csi from "csi/main";
import reflection from "csi/reflection";
import { resolveNode, resolveNodes } from "csi/service/nodeService";
import Cache from "csi/store/Cache";
import ErrorText from "csi/widget/ErrorText";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import { ButtonEnum } from "designSystem/types/button-types";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import ProductSelectProductHierarchyForm from "src/SiteAdmin/ProductHierarchy/ProductSelectProductHierarchyForm";

const AUTO_CREATE_PRODUCT_SKU = "AutoCreateProductSKU";
const AUTO_CREATE_SKU = "AutoCreateSKU";
const COPY_COUNT = "CopyCount";
const COPY_SKUS = "CopySKUs";
const ENABLE_CREATE_PRODUCT_IN_PROJECT_OFF = "EnableCreateProductInProject:Off";
const ENABLE_CREATE_PRODUCT_IN_PROJECT_REQUIRED = "EnableCreateProductInProject:Required";
const ERROR_REQUIRED = "RequiredValue";
const FOOD_ADMINISTRATOR = "Food Administrator";
const FOOD_SUBTYPE = "Subtype";
const FOOD_SKU_VIEW = "ProductSKU";
const SITE_LEVEL_URL = "centric://REFLECTION/INSTANCE/_CS_Data/null";
const NUM_TO_CREATE = "NumToCreate";
const OFF = "Off";
const PRODUCT_HIERARCHY = "ProductHierarchy";
const PRODUCT_HIERARCHY_DETAILS = "ProductHierarchyDetails";
const PRODUCT_HIERARCHY_FOLDER = "ProductHierarchyFolder";
const PROJECT_CREATE_ROLES = ["Project Administrator", "Project Editor"];
const PROJECT_LINE_ITEMS = "ProjectLineItems";
const PROJECT_LINE_ITEM_TO_SKU = "ProjectLineItemToSKU";
const PROJECT = "Project";
const PROMPT = "Prompt";
const HIERARCHY_TYPES = ["ProductHierarchy", "ProductHierarchyFolder"];

const {
	BUTTON_SAVE_GO,
} = ButtonEnum;

const FoodForm = declare(ProductSelectProductHierarchyForm, {

	/** @property {string} foodUrl - url of the Food used in this form */
	foodUrl: csi.EMPTY_URL,

	/** @property {string} productType - type of product shown in view. Example: "Food" */
	productType: "Food",

	/** @property {Field} projectField - Project field */
	projectField: null,

	/** @property {C8Node} _hierarchyNode - node for top level hierarchy if in hierarchy */
	_hierarchyNode: null,

	/**
	 * @override
	 * Insert Food.Subtype field if it hasn't been added to the FormDefinition
	 * Insert AutoCreateSKU field
	 */
	_resolveView() {
		const fieldSubtype = this.insertFieldIfNotExist(reflection.ATTR_SUBTYPE, 0);

		if (this.isNew()) {
			this.insertField(lang.mixin(new FieldConstructor(), {
				m_label: AUTO_CREATE_SKU,
				m_typeControl: def.FIELD_CHECKBOX,
			}), locale.resource(PRODUCT_HIERARCHY));

			this.addDefaultValue(AUTO_CREATE_SKU, true);
		}

		// Used to get RealizedProducts (FoodSKUs) and NodeType
		const sourceNode = this.defView.getSourceNode();
		const cache = this.getCache();
		this.foodUrl = sourceNode?.$URL || this.foodUrl;
		if (this.isCopy()) {
			// If sourceNode is null, just assume no skus to copy
			const numActiveFoodSKUs = sourceNode?.RealizedProducts.filter((product) => cache.get(product)?.Active).length || 0;
			// Disabled if 0, Singular if 1, Plural > 1
			this.insertField(lang.mixin(new FieldConstructor(), {
				m_label: COPY_SKUS,
				m_typeControl: def.FIELD_MULTI_SELECT,
				widgetParams: {
					addItemAll: numActiveFoodSKUs > 0,
					defaultAll: false,
					showClearField: false,
					defaultDisplayValue: numActiveFoodSKUs == 0 ? locale.resource("ProductHasNFoodSkus", [0]) : locale.resource("Select"),
				},
			}), locale.resource(PRODUCT_HIERARCHY));

			if (numActiveFoodSKUs == 0) {
				this.setFieldDisabled(COPY_SKUS, true);
			}
		}

		const action = this.getSourceController().getAction()
			.getId();

		let quantityFieldInfo = "";
		if (this.isNew() || action === "NewFromFood" ) {
			quantityFieldInfo = "QuantityField_NewForm_InfoText_Food";
		} else {
			// Copy case
			quantityFieldInfo = "QuantityField_CopyForm_InfoText_Food";
		}
		const _quantityFieldText = declare(ErrorText, {
			infoResource: quantityFieldInfo,
		});
		this.insertField(lang.mixin(new FieldConstructor(), {
			m_label: this.isNew() || action === "NewFromFood" ? NUM_TO_CREATE : COPY_COUNT,
			m_onValidate: "_onValidateNumToCreate",
			messageClass: _quantityFieldText,
		}), this.isCopy() ? COPY_SKUS : AUTO_CREATE_SKU);

		this.addDefaultValue(NUM_TO_CREATE, 1);
		this.addDefaultValue(COPY_COUNT, 1);
		this.defView.getField(reflection.ATTR_SUBTYPE).m_onChange = "_onChangeSubtype";

		const nodeType = this.defView.getSource()?.getParent()?.$Type;

		if (sourceNode) {
			fieldSubtype.m_right = xml.RIGHT_NONE;
		}

		this.addProjectField();

		// Show ProductHierarchy field store if form is invoked on the site-level or in project
		if (this.formInfo?.contextUrl === SITE_LEVEL_URL ||
			this.formInfo?.parentAttributeId === PROJECT_LINE_ITEMS ||
			(HIERARCHY_TYPES.includes(nodeType) && action !== "Edit")) {
			this.inherited(arguments);
		} else {
			Widget.prototype._resolveView.call(this);
		}
	},

	/**
	 * Validate that creation quantity is within range
	 * @param defView
	 * @param field
	 * @param value
	 * @param params
	 */
	_onValidateNumToCreate(defView: View, field: Field, value: string, params: Object) {
		const numToCreateField = this.getField(NUM_TO_CREATE);
		const copyCountField = this.getField(COPY_COUNT);
		const action = this.getSourceController().getAction()
			.getId();

		let fieldValue: number = 1; // C8FB-1573: setting default copyCount and massCreateLimit to be 1
		let massCreateProductLimit = null;
		let multiCopyLimit = null;
		if (((this.isNew() || action === "NewFromFood") && numToCreateField) && this._hierarchyNode) {
			fieldValue = Number(numToCreateField.getValue());
			massCreateProductLimit = config.getSubtypeSetting(this._hierarchyNode.Subtype, "MassCreateProductLimit") as unknown as number;
		}
		if ((this.isCopy() && copyCountField)) {
			fieldValue = Number(copyCountField.getValue());
			const sourceNode = this.defView.getSourceNode();
			multiCopyLimit = sourceNode ?
				config.getSubtypeSetting(sourceNode.Subtype, "MultiCopyLimit") as unknown as number :
				config.getSubtypeSetting(this.getFieldValue(reflection.ATTR_SUBTYPE), "MultiCopyLimit") as unknown as number;
		}

		if (!value || Number.isNaN(Number(value)) || !Number.isInteger(fieldValue) || fieldValue < 1 || value.includes(".")) {
			throw new Error(locale.resource("FoodMassCreateError_Invalid"));
		}
		if (massCreateProductLimit && fieldValue > massCreateProductLimit && (this.isNew() || action === "NewFromFood")) {
			throw new Error(locale.resource("FoodMassCreateError_TooMany", [massCreateProductLimit]));
		}
		if (multiCopyLimit && fieldValue > multiCopyLimit && this.isCopy()) {
			throw new Error(locale.resource("FoodMassCopyError_TooMany", [multiCopyLimit]));
		}
		if (fieldValue < 1) {
			throw new Error(locale.resource("FoodMassCreateError_TooFew"));
		}
	},

	/**
	 * Add the Required style to the NUM_TO_CREATE, COPY_COUNT field
	 * @override
	 * @param tr
	 * @param bRequired
	 * @returns
	 */
	_addSeparator(tr: HTMLElement, bRequired: boolean) {
		return html.create("td", {
			class: "csiFormViewSeparator",
			innerText: bRequired || tr.dataset.csiFormField === NUM_TO_CREATE || tr.dataset.csiFormField === COPY_COUNT ? "*" : null,
		}, tr);
	},

	/**
	 * @override
	 * Hide Auto Create SKU field initially, hide Quantity field when not needed
	 * @param fields
	 */
	_buildFields(fields) {
		this.inherited(arguments);

		const action = this.getSourceController().getAction()
			.getId();
		const hierarchySubtype = this.getHierarchySubtype();
		const sourceNode = this.defView.getSourceNode();
		const multiCopyLimit = sourceNode ?
			config.getSubtypeSetting(sourceNode.Subtype, "MultiCopyLimit") as unknown as number :
			config.getSubtypeSetting(this.getFieldValue(reflection.ATTR_SUBTYPE), "MultiCopyLimit") as unknown as number;


		if (hierarchySubtype && (this.isNew() || action === "NewFromFood")) {
			const massCreateProductLimit = config.getSubtypeSetting(hierarchySubtype, "MassCreateProductLimit") as unknown as number;

			this.setFieldHidden(NUM_TO_CREATE, massCreateProductLimit === 1);
		} else {
			this.setFieldHidden(NUM_TO_CREATE, true);
		}

		if (this.isCopy() && action !== "NewFromFood") {
			this.setFieldHidden(COPY_COUNT, multiCopyLimit === 1);
		}

		if (this.isNew()) {
			this.setFieldHidden(AUTO_CREATE_SKU, true);
			this.setFieldHidden(COPY_COUNT, true);
		}

		if (this.isEdit()) {
			this.setFieldHidden(NUM_TO_CREATE, true);
			this.setFieldHidden(COPY_COUNT, true);
		}
	},

	/**
	 * @override
	 * Gets called after the view finishes building
	 * If the current view is the top level of a Product Hierarchy, automatically set the selected Product Hierarchy
	 */
	async onViewEnd() {
		const currentNode = this.defView.getSource()?.getParent();

		if (!currentNode) {
			return;
		}

		if (this.isCopy()) {
			const projects = this.defView.getSourceNode()?.Projects;
			if (projects && projects.length > 0) {
				let oneProject = true;
				const projectLineItems = await resolveNodes(projects);
				const firstProject = projectLineItems[0].__Parent__;
				for (let n = 1; n < projectLineItems.length; n++) {
					if (firstProject !== projectLineItems[n].__Parent__) {
						oneProject = false;
						break;
					}
				}
				if (oneProject === true) {
					this.setFieldValue(PROJECT, firstProject);
				}
			}
		}

		const nodeType = this.defView.getSource()?.getParent()?.$Type;
		if (HIERARCHY_TYPES.includes(nodeType) || nodeType == PRODUCT_HIERARCHY_DETAILS) {
			this._hierarchyParents = await this._getRootHierarchyNode(currentNode, []);
			this._currentLevel = currentNode.Level ? currentNode.Level : 0;
			this._hierarchyNode = this._hierarchyParents[0][0];

			if (this._hierarchyNode.$URL && HIERARCHY_TYPES.includes(currentNode.$Type)) {
				// With the 'quiet' parameter set to false, the setFieldValue call will trigger '_onChangeProductHierarchy'
				this.setFieldValue(this._fieldHierarchy, this._hierarchyNode.$URL, false);
				this.setFieldDisabled(this._fieldHierarchy, true);
			}
			const massCreateProductLimit = config.getSubtypeSetting(this._hierarchyNode.Subtype, "MassCreateProductLimit") as unknown as number;
			if (massCreateProductLimit == 1) {
				this.setFieldHidden(NUM_TO_CREATE, true);
			}
		} else { // Not in Hierarchy
			this.setFieldHidden(NUM_TO_CREATE, true);
		}
	},

	async _getRootHierarchyNode(currentNode, hierarchyParents) {
		const subtype = await resolveNode(currentNode.Subtype);
		hierarchyParents = [...[[currentNode, subtype]], ...hierarchyParents];
		const parentURL = currentNode.__Parent__;

		if (xml.isEmptyURL(parentURL)) {
			return hierarchyParents;
		}

		const parentNode = await resolveNode(parentURL);
		return await this._getRootHierarchyNode(parentNode, hierarchyParents);
	},

	/**
	 * Hide or expose Auto Create SKU field depending on subtype
	 * @param defView
	 * @param field
	 * @param value
	 */
	_onChangeSubtype(defView, field, value) {
		if (this.isNew()) {
			const autoCreateSKU = xml.enumGetText(config.getSubtypeSetting(value, AUTO_CREATE_PRODUCT_SKU) as unknown as string);
			const autoCreateSKUField = this.getField(AUTO_CREATE_SKU);
			const autoCreateSKUDisabled = (autoCreateSKU !== PROMPT);
			if (this.projectField && this.projectField.messageNode.domNode.innerHTML !== locale.resource(ERROR_REQUIRED)) {
				this.setProjectInfoMessage(value);
			}

			if (autoCreateSKU && autoCreateSKU != OFF) {
				this.setFieldValue(autoCreateSKUField, true);
				this.setFieldHidden(autoCreateSKUField, false);
				this.setFieldDisabled(autoCreateSKUField, autoCreateSKUDisabled);
			} else if (autoCreateSKU && autoCreateSKU === OFF) {
				this.setFieldHidden(autoCreateSKUField, true);
			}
		}
	},

	/**
	 * sets the project info message based on food subtype selected in form
	 * @param foodSubtype - food subtype currently selected
	 */
	setProjectInfoMessage(foodSubtype) {
		const projectItemToSKU = xml.enumGetText(config.getSubtypeSetting(foodSubtype, PROJECT_LINE_ITEM_TO_SKU) as unknown as string);
		const projectItemToSKUText = projectItemToSKU === "None" ? "None" : "AssociateSKUs";
		const autoCreateSKU = xml.enumGetText(config.getSubtypeSetting(foodSubtype, AUTO_CREATE_PRODUCT_SKU) as unknown as string);

		const infoMessage = "ProjectLineItemToSKU_NewForm_InfoText_" + projectItemToSKUText;
		const projectMessageNode = this.projectField.messageNode;
		if (this.projectField) {
			if (autoCreateSKU && autoCreateSKU != OFF) {
				projectMessageNode.set("infoResource", infoMessage);
			} else {
				projectMessageNode.set("infoResource", "");
			}
		}
	},

	/**
	 * Gets the Hierarchy Subtype of current node
	 * @returns Node of Hierarchy Subtype, null if not in Hierarchy
	 */
	getHierarchySubtype() {
		const hierarchyContext = this.getSourceContext();
		const cache = hierarchyContext.cache;
		// get the ProductHierarchyURL
		const hierarchyNode = hierarchyContext.getNode();
		let hierarchyURL;
		if (hierarchyNode) {
			switch (hierarchyNode.$Type) {
				case PRODUCT_HIERARCHY_DETAILS:
					hierarchyURL = hierarchyNode.ProductHierarchy;
					break;
				case PRODUCT_HIERARCHY:
					hierarchyURL = hierarchyNode.$URL;
					break;
				case PRODUCT_HIERARCHY_FOLDER:
					hierarchyURL = hierarchyNode.HierarchyParents.first();
					break;
			}
		}
		return hierarchyURL ? cache?.getNode(hierarchyURL)?.Subtype : null;
	},

	/**
	 * Handle Project field on change of project
	 * @param {defView} view- view
	 * @param {field} field - field on the form
	 * @param {projectValue} string - value of the field
	 */
	_onChangeProject(defView, field, projectValue) {
		const foodSubtype = this.getFieldValue(FOOD_SUBTYPE);
		if (foodSubtype && field.messageNode.domNode.innerHTML === locale.resource(ERROR_REQUIRED)) {
			this.setProjectInfoMessage(foodSubtype);
		}

		if (!projectValue || !xml.isValidURL(projectValue)) {
			return;
		}

		// Check for global access
		if (crews.isInGlobalRoles(PROJECT_CREATE_ROLES)) {
			return;
		}

		// Check edit access for the specific project
		const cache = this.getCache() as Cache;
		const projectNode = cache.getNode(projectValue);
		if (crews.isInRoles(PROJECT_CREATE_ROLES, projectNode)) {
			return;
		} else {
			throw new Error(locale.resource("ProjectError_NotEditable"));
		}
	},

	/*
	 * Insert Project field to form
	 */
	addProjectField() {
		// Do not display "Project" field on edit form
		if (this.isEdit()) {
			return;
		}

		// Setup for adding additional fields to new Food form in Product Hierarchy
		const hierarchySubtypeURL = this.getHierarchySubtype();

		// Check for adding Project field
		const enableCreateProductInProject = config.getSubtypeSetting(hierarchySubtypeURL, "EnableCreateProductInProject");
		const parentContext = this.getSourceController().getContext();
		const parentContextType = parentContext.defView.m_idType;
		const contextURL = this.formInfo?.contextUrl;

		if (enableCreateProductInProject !== ENABLE_CREATE_PRODUCT_IN_PROJECT_OFF &&
			parentContextType != PROJECT &&
			contextURL != SITE_LEVEL_URL) {
			const requiredNew: boolean = enableCreateProductInProject === ENABLE_CREATE_PRODUCT_IN_PROJECT_REQUIRED;

			this.projectField = lang.mixin(new FieldConstructor(), {
				m_bRequired: requiredNew,
				m_id: PROJECT,
				m_label: locale.type(PROJECT),
				m_typeControl: def.FIELD_FILTERING_SELECT,
				m_onValidate: "_onChangeProject",
			});

			this.insertField(this.projectField, this.isCopy() ? COPY_SKUS : AUTO_CREATE_SKU);
		}
	},

	/**
	 * @override
	 * Create field store depending on type of field
	 */
	getFieldStore(field, value) {
		if (field.m_id === PROJECT) {
			return this.getProjectFieldStore(field);
		} else if (field.getControlName() === COPY_SKUS) {
			return this.getFoodSKUStore(field);
		}

		return this.inherited(arguments);
	},

	getFoodSKUStore(field: Field) {
		// Create relationship for Active Food SKU
		const q = new Query()
			.Attr("Active", xml.OP_EQUALS, true);

		const rel = lang.mixin(new Relationship(), {
			m_url: this.foodUrl,
			m_id: "RealizedProducts",
			m_typeNode: "FoodSKU",
			m_query: q,
		});

		// Create View For Go To Dialog
		const ctx = this.getContext();
		const store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);

		store.action = lang.mixin(new Action(), {
			m_fixedQuery: q,
			m_viewRef: lang.mixin(new ViewRef(), {
				m_id: FOOD_SKU_VIEW,
				m_url: this.foodUrl,
			}),
		});

		return store;
	},

	/**
	 * Create field store for Project field
	 * @param field
	 */
	getProjectFieldStore(field) {
		const viewRefId = "ProjectsWithViewAccess";
		const viewRefURL = "centric://REFLECTION/INSTANCE/_CS_Data/null";
		const ctx = this.getContext();
		const rel = lang.mixin(new Relationship(), {
			m_typeNode: PROJECT,
			m_module: "SiteAdmin",
			m_operation: "GetProjectsWithViewAccess",
		});

		let store;
		let priorityStore;

		if (this.isCopy()) {
			// Priority store for Projects associated with Parent Food.
			priorityStore = ctx.view.createHeadingNamesStore(
				ctx,
				field,
				null,
				lang.mixin(new Relationship(), {
					m_typeNode: PROJECT,
					m_module: "FormulatedProduct",
					m_operation: "GetProjectsForFood",
					m_url: this._getParentURLOfClientNode(),
					m_sorts: [{ id: xml.ATTR_NAME }],
				}),
			);

			store = ctx.view.createHeadingNamesStore(ctx, field, null, rel, null, {
				priorityStore: priorityStore,
				defaultToPriorityOnly: true,
			});
		} else {
			store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);
		}

		store.action = lang.mixin(new Action(), {
			m_viewRef: lang.mixin(new ViewRef(), {
				m_id: viewRefId,
				m_url: viewRefURL,
			}),
		});

		return store;
	},

	/**
	 * @override
	 * Override to filter store
	 * @param field
	 * @returns StoreNames
	 */
	getProductHierarchyFolderStore(field: Field) {
		const prevValue = this.getPrevLevelValue(field.m_name);
		const ctx = this.getContext();
		if (prevValue === csi.EMPTY_URL) {
			return ctx.view.createHeadingNamesStore(ctx, field, null);
		}
		const rel = lang.mixin(new Relationship(), {
			m_module: "SiteAdmin",
			m_operation: "ProductHierarchyLevelGetLeafFoldersWithRole",
			m_typeNode: "ProductHierarchyFolder",
			m_parameters: { URL: prevValue, Role: FOOD_ADMINISTRATOR },
		});

		// create view for goto dialog
		const store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);

		return store;
	},
});

export = FoodForm;
